import React, { lazy, Suspense, useEffect, useState, useRef} from 'react';
import { Route, Redirect, BrowserRouter as Router, Switch, useLocation } from 'react-router-dom';
import { Modal, Checkbox } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { PUBLIC_ROUTE } from './route.constants';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';
import { parse, stringify } from 'query-string';
import { browserName, browserVersion } from 'react-device-detect';

// Components
import Loader from 'src/components/utility/loader';
import ErrorBoundary from './ErrorBoundary';
import Auth from 'src/components/Auth';

// Styles
import { ContentOutdateVersion } from './local.styles';

const { confirm } = Modal;

const Dashboard = lazy(() => import('./containers/Dashboard/Dashboard'))
const Page404 = lazy(() => import('./containers/Pages/404'))

const publicRoutes = [
  {
    path: PUBLIC_ROUTE.LANDING,
    exact: true,
    component: lazy(() => import('src/containers/Pages/Login')),
  },
  {
    path: PUBLIC_ROUTE.SIGN_IN,
    exact: true,
    component: lazy(() => import('src/containers/Pages/Login')),
  },
  {
    path: PUBLIC_ROUTE.AUTH_PROCESSING,
    exact: true,
    component: lazy(() => import('src/containers/Pages/AuthProcessing')),
  },
  {
    path: PUBLIC_ROUTE.SIGN_UP,
    exact: true,
    component: lazy(() => import('src/containers/Pages/Signup')),
  },
  {
    path: PUBLIC_ROUTE.SIGN_UP_SUCCESS,
    exact: true,
    component: lazy(() => import('src/containers/Pages/Signup/SignupSuccess')),
  },
  {
    path: PUBLIC_ROUTE.SIGN_UP_COMPLETE,
    exact: true,
    component: lazy(() => import('src/containers/Pages/CreatePassword')),
  },
  {
    path: PUBLIC_ROUTE.FORGET_PASSWORD,
    exact: true,
    component: lazy(() => import('src/containers/Pages/ForgetPassword')),
  },
  {
    path: PUBLIC_ROUTE.CREATE_ORG,
    exact: true,
    component: lazy(() => import('src/containers/Pages/AddOrg')),
  },
  {
    path: PUBLIC_ROUTE.CREATE_ORG_SUCCESS,
    exact: true,
    component: lazy(() => import('src/containers/Pages/AddOrg/AddOrgSuccess')),
  },
  {
    path: PUBLIC_ROUTE.JOIN_ORG,
    component: lazy(() => import('src/containers/Pages/Loading')),
  },
  {
    path: PUBLIC_ROUTE.CREATE_ACCOUNT,
    component: lazy(() => import('src/containers/Pages/CreateAccount')),
  },
  {
    path: PUBLIC_ROUTE.PAGE_403,
    component: lazy(() => import('src/containers/Pages/403')),
  },
  {
    path: PUBLIC_ROUTE.PAGE_404,
    component: lazy(() => import('src/containers/Pages/404')),
  },
  {
    path: PUBLIC_ROUTE.PAGE_500,
    component: lazy(() => import('src/containers/Pages/500')),
  },
  {
    path: PUBLIC_ROUTE.RESET_PASSWORD,
    component: lazy(() => import('./containers/Pages/ResetPassword')),
  },
  {
    path: PUBLIC_ROUTE.PRIVACY_POLICY,
    exact: true,
    component: lazy(() => import('./containers/Pages/PrivacyPolicy')),
  },
]

function PrivateRoute({ children, ...rest }) {
  let location = useLocation()
  const isLoggedIn = Auth.isLoggedIn()
  if (isLoggedIn) return <Route {...rest}>{children}</Route>
  return (
    <Redirect
      to={{
        pathname: '/login',
        state: { from: location },
      }}
    />
  )
}

function shouldShowWarning() {
  const stored = localStorage.getItem('hideVersionWarningUntil');
  if (!stored) return true;

  const hideUntil = new Date(stored);
  return new Date() > hideUntil;
}

function hideWarningForOneMonth() {
  const oneMonthLater = new Date();
  oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
  localStorage.setItem('hideVersionWarningUntil', oneMonthLater.toISOString());
}

function isOldBrowser() {
  const minVersions = {
    Chrome: 92,
    Edge: 92,
    Safari: 15.4,
    Firefox: 90,
  };

  const currentVersion = parseFloat(browserVersion);
  const minRequiredVersion = minVersions[browserName];
  if (minRequiredVersion === undefined) return false;

  return currentVersion < minRequiredVersion;
}

export default function Routes() {
  const checkboxRef = useRef(null);

  useEffect(() => {
    if (shouldShowWarning() && isOldBrowser()) {
      const showConfirm = () => {
        confirm({
          title: 'Phiên bản trình duyệt đã quá cũ!',
          icon: <ExclamationCircleOutlined />,
          content: <ContentOutdateVersion>
            <p>Vui lòng nâng cấp phiên bản mới hơn để tránh xảy ra lỗi.</p>
            <Checkbox ref={checkboxRef}>Ẩn thông báo trong 1 tháng</Checkbox>
          </ContentOutdateVersion>,
          onOk() {
            checkboxRef.current?.input?.checked && hideWarningForOneMonth();
          },
          okText: 'Tiếp tục',
          okCancel: false,
        });
      };
      showConfirm();
    }
  }, []);

  return (
    <Router
      getUserConfirmation={() => {
        /* Empty callback to block the default browser prompt */
      }}
    >
      <ErrorBoundary>
        <Suspense fallback={<Loader />}>
          <QueryParamProvider
            adapter={ReactRouter5Adapter}
            options={{
              searchStringToObject: parse,
              objectToSearchString: stringify,
            }}
          >
            <Switch>
              {publicRoutes.map((route, index) => (
                <Route key={index} path={route.path} exact={route.exact}>
                  <route.component />
                </Route>
              ))}
              <PrivateRoute path='/'>
                <Dashboard />
              </PrivateRoute>
              <Route component={Page404} />
            </Switch>
          </QueryParamProvider>
        </Suspense>
      </ErrorBoundary>
    </Router>
  )
}
